import { Injectable, inject } from '@angular/core';
import type { ContactReason } from '../marketing/contact-reason.constants';
import { PUBLIC_MARKETING_RUNTIME_CONFIG } from '../tokens/public-marketing-runtime.token';

export type PublicLeadKind = 'register_interest' | 'contact';

export interface PublicLeadSubmissionBody {
  kind: PublicLeadKind;
  recaptchaToken: string;
  name: string;
  email: string;
  organisation: string;
  message: string;
  /** Required when `kind` is `contact`. Omitted for `register_interest`. */
  reason?: ContactReason;
}

/** Thrown when public lead POST fails; `requestDiagnostics` is shown in the debug alert. */
export class PublicLeadSubmissionError extends Error {
  override readonly name = 'PublicLeadSubmissionError';

  constructor(
    message: string,
    public readonly requestDiagnostics: string,
  ) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

@Injectable({ providedIn: 'root' })
export class PublicLeadSubmissionService {
  private readonly marketing = inject(PUBLIC_MARKETING_RUNTIME_CONFIG);

  async submit(body: PublicLeadSubmissionBody): Promise<{ id: string; ok: boolean }> {
    const base = (this.marketing.apiUrl ?? '').replace(/\/$/, '');
    if (!base) {
      throw new PublicLeadSubmissionError(
        'API URL is not configured',
        'No apiUrl in PUBLIC_MARKETING_RUNTIME_CONFIG; cannot build request URL.',
      );
    }
    const urlObj = new URL(`${base}/public_lead_submissions`);
    urlObj.searchParams.set('ngsw-bypass', 'true');
    const requestUrl = urlObj.toString();

    let res: Response;
    try {
      res = await fetch(requestUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (cause) {
      const causeMsg = cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause);
      const causeStack =
        cause instanceof Error && cause.stack ? `\nCause stack:\n${cause.stack}` : '';
      throw new PublicLeadSubmissionError(
        cause instanceof Error ? cause.message : 'Network request failed',
        [
          `Request URL: ${requestUrl}`,
          'Method: POST',
          'Headers: Content-Type: application/json',
          '(Body omitted; contains personal data.)',
          '',
          `Fetch threw before HTTP response: ${causeMsg}${causeStack}`,
        ].join('\n'),
      );
    }

    const rawText = await res.text();
    let data: { id?: string; ok?: boolean; error?: { message?: string } } = {};
    if (rawText) {
      try {
        data = JSON.parse(rawText) as typeof data;
      } catch {
        /* non-JSON body */
      }
    }

    const bodyPreview = rawText.length > 1200 ? `${rawText.slice(0, 1200)}…` : rawText;

    if (!res.ok) {
      const msg = data?.error?.message ?? `Request failed (${res.status})`;
      throw new PublicLeadSubmissionError(
        msg,
        [
          `Request URL: ${requestUrl}`,
          `HTTP status: ${res.status} ${res.statusText}`,
          'Response body:',
          bodyPreview || '(empty)',
        ].join('\n'),
      );
    }

    if (!data.id) {
      throw new PublicLeadSubmissionError(
        'Invalid response from server',
        [
          `Request URL: ${requestUrl}`,
          `HTTP status: ${res.status} (expected JSON with id)`,
          'Response body:',
          bodyPreview || '(empty)',
        ].join('\n'),
      );
    }

    return { id: data.id, ok: !!data.ok };
  }
}
